import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { ViewportScroller } from '@angular/common';
import { withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  makeEnvironmentProviders,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  type ApplicationConfig,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { NGX_SKELETON_LOADER_CONFIG } from 'ngx-skeleton-loader';

import { provideAppName, skeletonConfig } from '@cosmos/core';
import { provideThemes } from '@cosmos/data-access-themes';
import { OptInPreloadStrategy } from '@cosmos/router';
import { CosTheme } from '@cosmos/types-common';
import { provideCosIcon } from '@cosmos/ui-icon';
import { provideCosmosHttpClient } from '@cosmos/util-http';
import { provideI18nDateFormats } from '@cosmos/util-i18n-dates';
import { provideVisitTrackerSkipDetection } from '@customer-portal/common/data-access-visit-notification';
import { CustomerPortalViewportScroller } from '@customer-portal/common/util-layout';
import { providePreviewData } from '@customer-portal/data-access-preview';
import { routes } from '@customer-portal/feature-shell-web';

import { makeCoreEnvironmentProviders } from './core';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppName('customer-portal'),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),

    provideCosIcon(),

    // `BrowserAnimationsModule`.
    provideAnimations(),

    // `HttpClientModule`.
    provideCosmosHttpClient(withXhr(), withInterceptorsFromDi()),

    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      // Open every page at the top and restore the offset on browser
      // back/forward. Only works together with the `ViewportScroller` override
      // below — the document does not scroll in this app.
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
      }),
      withPreloading(OptInPreloadStrategy)
    ),

    makeEnvironmentProviders([
      // The app scrolls `customer-portal-projects-host`, not the document, so
      // the router's scrolling has to target that element.
      { provide: ViewportScroller, useClass: CustomerPortalViewportScroller },
      { provide: NGX_SKELETON_LOADER_CONFIG, useValue: skeletonConfig() },
      // Since CDK v.21, the default behavior of the overlay is to use popover strategy,
      // which causes some issues in 3rd party payments dialogs, so we need to set it to false.
      // The popover creates a stacking context which causes the z-index of the payment dialogs to be lower and thus the payment dialog is hidden behind the payment-button dialog.
      {
        provide: OVERLAY_DEFAULT_CONFIG,
        useValue: { usePopover: false },
      },
    ]),

    providePreviewData(),

    // Resolves whether this document should be excluded from visit tracking,
    // e.g. when opened by the distributor "Customer View" CTA.
    provideVisitTrackerSkipDetection(),

    makeCoreEnvironmentProviders(),

    provideI18nDateFormats(),

    provideCosIcon(),

    provideThemes({
      defaultTheme: CosTheme.CustomerPortal,
      appId: 'customer-portal',
    }),
  ],
};
